import React, { FC, Fragment, useEffect, useState } from 'react';
import { captureException } from '@sentry/react';
import { Col, Input, Modal, Row } from 'antd';
import { TbCircleXFilled, TbSearch, TbUserSearch, TbX } from 'react-icons/tb';
import { useDebounce } from 'react-use';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { memberService } from '@/services/database';
import { SkeletonList } from '@/shared';
import { Member, SetStateMethod, Team } from '@/types';
import { centerComponentInMiddleofViewWithoutAlignItems } from '@/utilities';

import ListItem from './ListItem';
import { VirtualizedList as List } from '@/shared';

interface InviteToTeamModalProps {
    open: boolean;
    setOpen: SetStateMethod<boolean>;
    collaborators: any;
    setCollaborators: any;
    team: Team;
}

const InviteToTeamModal: FC<InviteToTeamModalProps> = ({
    open,
    setOpen,
    collaborators,
    setCollaborators,
    team,
}): JSX.Element => {
    const [loading, setLoading] = useState(true);

    const [input, setInput] = useState('');

    const [results, setResults] = useState<Member[]>([]);

    const handleWorkspaceMembersSearch = (value: string) => {
        try {
            memberService.searchTeamInviteCandidates(value).then((members) => {
                setResults(members);
            });
        } catch (error) {
            captureException(error);
        } finally {
            setLoading(false);
        }
    };

    const [, cancel] = useDebounce(
        () => {
            if (input !== '') {
                setLoading(true);

                handleWorkspaceMembersSearch(input);
            } else if (input === '') {
                setLoading(false);

                setResults([]);
            }
        },
        500,
        [input],
    );

    useEffect(() => {
        setLoading(false);
    }, []);

    const { token } = useAntDesignTheme();

    return (
        <>
            <Modal
                title='Invite Members to Team'
                closeIcon={<TbX size='1rem' style={{ verticalAlign: '-0.2rem' }} />}
                open={open}
                onOk={() => setOpen(false)}
                onCancel={() => setOpen(false)}
                style={{ minWidth: '37.5vw' }}
                bodyStyle={{ minHeight: '37.5vh' }}
                footer={null}
                mask={false}
            >
                <Input
                    prefix={
                        <TbSearch size='1rem' style={{ color: token.colorTextQuaternary, marginRight: '0.5rem' }} />
                    }
                    placeholder='Search Workspace Members...'
                    size='large'
                    value={input}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        setLoading(true);

                        setInput(e.target.value);
                    }}
                    allowClear={{
                        clearIcon: (
                            <TbCircleXFilled
                                size='1.125rem'
                                style={{ color: token.colorTextTertiary, verticalAlign: '-0.25rem' }}
                            />
                        ),
                    }}
                />
                {results?.length !== 0 ? (
                    <List
                        dataSource={results}
                        renderItem={(result: any) => {
                            return (
                                <Fragment key={result?.id}>
                                    <ListItem
                                        result={result}
                                        open={open}
                                        setOpen={setOpen}
                                        collaborators={collaborators}
                                        setCollaborators={setCollaborators}
                                        team={team}
                                    />
                                </Fragment>
                            );
                        }}
                    />
                ) : loading ? (
                    <SkeletonList listLength={5} minHeight='35vh' />
                ) : (
                    <Row style={{ ...centerComponentInMiddleofViewWithoutAlignItems, height: '15vh' }}>
                        <Col span={6} />
                        <Col span={12} style={{ textAlign: 'center', margin: '0 auto' }}>
                            <TbUserSearch size='2.5rem' style={{ color: token.colorTextTertiary }} />
                            <br />
                            <span style={{ color: token.colorTextTertiary }}>
                                Search for members to add to your team by name, username, or email address.
                            </span>
                        </Col>
                        <Col span={6} />
                    </Row>
                )}
            </Modal>
        </>
    );
};

export default InviteToTeamModal;
